import { GeocodeRequester, type LatLng, type OsmAutocompleteResult, type ResolvedPosition } from '@cobliteam/api-lib';
import type { GoogleGeocoderResult } from '@cobliteam/common-types/src/google';

import { Environment } from '@cobliteam/react-environments';
import { type QueryKey, type UseQueryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getGeocodeRequester = () => new GeocodeRequester({ host: env.herbieApiHost });

export const QUERY_KEY: QueryKey = ['GEOCODE'];

export type LatLngAddress = {
  lat: number;
  lng: number;
  address: string;
};

const useGeocode = () => {
  const getAddress = async (latLng: LatLng) => {
    const address = await getGeocodeRequester().getAddress(latLng);
    return address;
  };

  const getLatLng = async (address: string) => {
    const latLng: LatLng = await getGeocodeRequester().geocodeFromAddress(address);
    return latLng;
  };

  // OSM-39: autocomplete self-hosted (osm-enhanced via proxy do herbie-geocoder).
  // `signal` (opcional) aborta a requisição em voo quando uma query mais nova dispara.
  const autocomplete = async (query: string, bias?: LatLng, signal?: AbortSignal): Promise<OsmAutocompleteResult[]> => {
    return getGeocodeRequester().autocomplete(query, bias, signal);
  };

  // OSM-83: refina um candidato OSM approximate pro motor de interpolação de
  // número (gateway público, ver GeocodeRequester.resolveNumber). `null` = sem
  // via/âncora pra esse cep (204) — caller mantém o candidato approximate original.
  const resolveNumber = async (cep: string, numero: number): Promise<ResolvedPosition | null> => {
    return getGeocodeRequester().resolveNumber(cep, numero);
  };

  const useGoogleGeocoder = (latLng: LatLng, options?: Partial<UseQueryOptions<GoogleGeocoderResult>>) => {
    // Google Maps é lazy por rota (APIProvider @vis.gl/react-google-maps): `google.maps.Geocoder`
    // não existe até a lib 'geocoding' carregar. Sem esse gate, `new google.maps.Geocoder()` estoura
    // TypeError no primeiro render de rotas com mapa. Ver commit af863d0664.
    const geocodingLibrary = useMapsLibrary('geocoding');

    return useQuery<GoogleGeocoderResult>({
      queryKey: [...QUERY_KEY, 'GOOGLE_GEOCODER', latLng.lat, latLng.lng],
      queryFn: async () => {
        if (!geocodingLibrary) throw new Error('Google geocoder is not ready');
        const geocoder = new geocodingLibrary.Geocoder();
        const response = await geocoder.geocode({ location: { lat: latLng.lat, lng: latLng.lng } });
        return response.results[0];
      },
      ...options,
      enabled: (options?.enabled ?? true) && geocodingLibrary !== null,
    });
  };

  return {
    getAddress,
    getLatLng,
    useGoogleGeocoder,
    autocomplete,
    resolveNumber,
  };
};

const useGetAddress = ({ lat, lng }: LatLng, options?: Partial<UseQueryOptions<LatLngAddress>>) => {
  return useQuery<LatLngAddress>({
    queryKey: [...QUERY_KEY, 'ADDRESS', lat, lng],
    queryFn: async () => {
      const address = await getGeocodeRequester().getAddress({ lat, lng });
      return { lat, lng, address };
    },
    ...options,
  });
};

const useGetAddresses = (latLngs: LatLng[], options?: Partial<UseQueryOptions<LatLngAddress>>) => {
  return useQueries({
    queries: latLngs.map((latLng) => ({
      queryKey: [...QUERY_KEY, 'ADDRESS', latLng.lat, latLng.lng],
      queryFn: async () => {
        const address = await getGeocodeRequester().getAddress(latLng);
        return { lat: latLng.lat, lng: latLng.lng, address };
      },
      ...options,
    })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data).filter((data): data is LatLngAddress => data !== undefined),
        pending: results.some((result) => result.isLoading),
      };
    },
  });
};

export default useGeocode;
export { useGetAddress, useGetAddresses };
